<template>
  <div>
    <h1 class="pb-0 mb-0">
      Digitale Checkliste bearbeiten
    </h1>
    <h3
      v-if="checklist"
      class="mt-0"
      v-text="checklist.title"
    />

    <mia-checklist-form
      :title="form.title"
      :items="form.items"
      @update-title="form.title = $event"
      @sort-changed="onSortChanged"
      @add-item="onAddItem"
      @update-item="onUpdateItem"
      @remove-item="onRemoveItem"
    />

    <div class="mt-8">
      <button
        class="inline-flex items-center block px-3 py-2 font-bold text-white rounded bg-orange-mia focus:outline-none focus:shadow-outline"
        :class="{
          'hover:bg-orange-600': isFormValid,
          'cursor-not-allowed opacity-75': !isFormValid
        }"
        :disabled="!isFormValid"
        type="button"
        @click="update"
      >
        Checkliste speichern
      </button>
    </div>

    <mia-spinner :is-visible="$wait.is('saving checklist')" />

    <mia-confirmation-modal
      v-if="itemToDelete !== null"
      id="confirm-delete-checklist-item-modal"
      confirm-classes="border-red-500 hover:border-red-600 text-red-500 hover:text-red-600"
      title="Element deaktivieren"
      @cancel="itemToDelete = null"
      @confirm="deleteItem"
    >
      <p class="p-4">
        Dieses Element der Checkliste wird endgültig deaktiviert und kann nicht mehr verwendet werden. Fortfahren?
      </p>
    </mia-confirmation-modal>
  </div>
</template>

<script>
import orderBy from 'lodash/orderBy'
import { updateChecklist } from '@/api/checklists'
import ChecklistForm from '@/components/checklists/ChecklistForm.vue'
import Spinner from '@/components/general/spinner.vue'

export default {
  name: 'MiaChecklistsEdit',

  components: {
    'mia-checklist-form': ChecklistForm,
    'mia-spinner': Spinner
  },

  data() {
    return {
      form: {
        title: '',
        items: []
      },
      itemToDelete: null
    }
  },

  computed: {
    checklist() {
      return this.$store.state.checklists.checklists.find(checklist => checklist.id === parseInt(this.$route.params.checklistId, 10))
    },

    isFormValid() {
      return this.form.title.length > 0 && this.form.items.length > 0
    }
  },

  watch: {
    checklist: {
      immediate: true,
      handler(checklist) {
        if (!checklist) return

        this.form.title = checklist.title
        this.form.items = checklist.items

        eventBus.$emit('mia-set-breadcrumb', [
          {
            link: {
              path: '/checklists'
            },
            text: 'Digitale Checklisten verwalten'
          },
          {
            link: {
              path: `/checklists/${this.checklist.id}`
            },
            text: this.checklist.title
          }
        ])
      }
    }
  },

  methods: {
    async update() {
      this.$wait.start('saving checklist')

      try {
        const checklist = await updateChecklist({
          ...this.form,
          id: this.checklist.id
        })

        this.$store.commit('setChecklist', checklist)

        eventBus.$emit('mia-info', {
          info: ['Digitale Checkliste wurde aktualisiert.']
        })
      } catch (e) {
        console.log('ERROR', e)
      } finally {
        this.$wait.end('saving checklist')
      }
    },

    async deleteItem() {
      await this.$store.dispatch('deleteChecklistItem', {
        checklistId: this.checklist.id,
        itemId: this.itemToDelete.id
      })

      this.itemToDelete = null

      eventBus.$emit('mia-info', {
        info: ['Element wurde deaktiviert.']
      })
    },

    onSortChanged(sorting) {
      this.form.items = this.form.items.map((item) => {
        const sortItem = sorting.find(({ id }) => id === item.id)

        if (sortItem) {
          // eslint-disable-next-line no-param-reassign
          item.index = sortItem.index
        }

        return item
      })

      this.form.items = orderBy(this.form.items, 'index')
    },

    onAddItem(item) {
      this.form.items.push(item)
    },

    onUpdateItem(updatedItem) {
      this.form.items = this.form.items.map((item) => {
        /* eslint-disable no-param-reassign */
        if (item.id === updatedItem.id) {
          item.content = updatedItem.content
          item.meta = updatedItem.meta
        }
        /* eslint-enable no-param-reassign */

        return item
      })
    },

    onRemoveItem(item) {
      this.itemToDelete = item
    }
  }
}
</script>
